import { useRef, useState, useEffect, memo } from "react";
import { findByIdInTreeList } from "../../utils/tree";
import { getTruncatedBreadcrumbs } from "../../utils/breadcrumbUtils";
import { Tooltip } from 'react-tooltip';
import BreadcrumbParentsMenu from "../BreadcrumbParentsMenu/BreadcrumbParentsMenu";

/**
 * EditModalHeader Component
 * 
 * Displays the header section of the Edit Modal containing:
 * - Breadcrumb navigation with dynamic truncation
 * - Hidden parents menu
 * - Close/back button
 * - Mobile long-press tooltips
 * 
 * @param {Object} props
 * @param {Array} props.parents - Array of parent items for breadcrumb trail
 * @param {Function} props.closeModal - Callback to close the modal
 * @param {boolean} props.isMobile - Whether the device is mobile
 * @param {Array} props.projects - Project tree data for navigation
 * @param {Function} props.dispatch - Redux dispatch function
 */
const EditModalHeader = ({ parents, closeModalRef, isMobile, projects, dispatch }) => {
    const breadcrumbContainerRef = useRef(null);
    const measurementContainerRef = useRef(null);
    const headingContainerRef = useRef(null);
    const controlsContainerRef = useRef(null);
    const ellipsisButtonRef = useRef();

    const [visibleBreadcrumbCount, setVisibleBreadcrumbCount] = useState(null);
    const [isMeasuring, setIsMeasuring] = useState(true);
    const [showParentsMenu, setShowParentsMenu] = useState(false);
    const [tooltipReady, setTooltipReady] = useState(false);

    const [touchTimer, setTouchTimer] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipContent, setTooltipContent] = useState('');
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, positionAbove: false, maxWidth: 300 });

    const handleTouchStart = (e) => {
        if (!isMobile) return;
        
        const target = e.currentTarget;
        const touch = e.touches[0];
        const rect = target.getBoundingClientRect();
        
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        const tooltipMaxWidth = Math.min(300, viewportWidth - 32);
        const estimatedTooltipHeight = 60;
        
        let x = rect.left + rect.width / 2;
        let y = rect.bottom + 8;
        
        const halfTooltipWidth = tooltipMaxWidth / 2;
        if (x - halfTooltipWidth < 16) {
            x = halfTooltipWidth + 16;
        } else if (x + halfTooltipWidth > viewportWidth - 16) {
            x = viewportWidth - halfTooltipWidth - 16;
        }
        
        let positionAbove = false;
        if (y + estimatedTooltipHeight > viewportHeight - 16) {
            y = rect.top - 8;
            positionAbove = true;
        }

        const timer = setTimeout(() => {
            const content = target.getAttribute('data-tooltip-content');
            
            setTooltipContent(content || '');
            setTooltipPosition({ x, y, positionAbove, maxWidth: tooltipMaxWidth });
            setShowTooltip(true);
            
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
            
            setTouchTimer(null);
        }, 500);
        setTouchTimer(timer);
    };

    const handleTouchEnd = () => {
        if (touchTimer) {
            clearTimeout(touchTimer);
            setTouchTimer(null);
        }
    };

    const handleTouchMove = () => {
        if (touchTimer) {
            clearTimeout(touchTimer);
            setTouchTimer(null);
        }
    };
    
    const handleTooltipClose = () => {
        setShowTooltip(false);
        setTooltipContent('');
        setTooltipPosition({ x: 0, y: 0, positionAbove: false, maxWidth: 300 });
    };

    const handleParentBreadcrumb = (projectId) => {
        const todoItem = findByIdInTreeList(projects, projectId);
        dispatch({ type: "TOGGLE_EDIT_MODAL", payload: { todo: todoItem } });
    };

    const toggleParentsMenu = () => {
        // Close mobile tooltip
        if (isMobile) {
            handleTooltipClose();
        }
        
        // Force close desktop tooltip by dispatching mouseleave event
        if (ellipsisButtonRef.current) {
            const event = new MouseEvent('mouseleave', { bubbles: true });
            ellipsisButtonRef.current.dispatchEvent(event);
            ellipsisButtonRef.current.blur();
        }
    
        setShowParentsMenu(!showParentsMenu);
    };

    const closeParentsMenu = () => {
        setShowParentsMenu(false);
    };

    const getHiddenParents = () => {
        if (!visibleBreadcrumbCount || visibleBreadcrumbCount >= parents.length) {
            return [];
        }
        return parents.slice(0, parents.length - visibleBreadcrumbCount);
    };

    // Close parents menu when parents change (navigation occurred)
    useEffect(() => {
        setShowParentsMenu(false);
    }, [parents]);

    // Delay tooltip initialization to prevent spurious tooltips on mount
    // 500ms delay ensures tooltip renders after async operations (like recurrence fetch) complete
    useEffect(() => {
        const timer = setTimeout(() => {
            setTooltipReady(true);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    // Dynamic breadcrumb truncation based on available space
    useEffect(() => {
        if (parents.length === 0) {
            setVisibleBreadcrumbCount(null);
            setIsMeasuring(false);
            return;
        }

        setIsMeasuring(true);

        const measureBreadcrumbs = () => {
            if (!breadcrumbContainerRef.current || !measurementContainerRef.current || 
                !headingContainerRef.current || !controlsContainerRef.current) {
                setVisibleBreadcrumbCount(null);
                setIsMeasuring(false);
                return;
            }

            const breadcrumbRect = breadcrumbContainerRef.current.getBoundingClientRect();
            const controlsRect = controlsContainerRef.current.getBoundingClientRect();
            const headingRect = headingContainerRef.current.getBoundingClientRect();
            
            const isMobileView = window.innerWidth <= 768;
            
            let containerWidth;
            
            if (isMobileView) {
                containerWidth = headingRect.width - 32;
            } else {
                containerWidth = controlsRect.left - breadcrumbRect.left;
            }
            
            const hiddenContainer = measurementContainerRef.current;
            hiddenContainer.style.width = `${containerWidth}px`;
            hiddenContainer.style.maxWidth = `${containerWidth}px`;
            
            void hiddenContainer.offsetHeight;
            
            const measurementItems = hiddenContainer.querySelectorAll('.gt-breadcrumb-item');
            
            if (measurementItems.length === 0) {
                setVisibleBreadcrumbCount(null);
                setIsMeasuring(false);
                return;
            }
            
            const itemWidths = [];
            measurementItems.forEach(item => {
                const width = item.offsetWidth;
                itemWidths.push(width);
            });
            
            const gapWidth = 4;
            const totalGaps = (itemWidths.length - 1) * gapWidth;
            const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0) + totalGaps;
            
            const buffer = isMobileView ? 5 : 10;
            const availableWidth = containerWidth - buffer;
            
            if (totalWidth <= availableWidth) {
                setVisibleBreadcrumbCount(null);
                setIsMeasuring(false);
                return;
            }
            
            const ellipsisTestItem = document.createElement('div');
            ellipsisTestItem.className = 'gt-breadcrumb-item';
            ellipsisTestItem.style.position = 'absolute';
            ellipsisTestItem.style.visibility = 'hidden';
            ellipsisTestItem.innerHTML = `
                <span class="gt-breadcrumb-separator">
                    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
                        <path d="M6.99951 5.50042L1.33416 9.68283e-08L-4.05157e-05 1.29536L4.33111 5.50042L-4.04154e-05 9.70464L1.33416 11L6.99951 5.50042Z" fill="#44546F"/>
                    </svg>
                </span>
                <button class="gw-icon-button gw-icon-button--icon">
                    <i class="gw-icon-button__icon gw-button__icon--more"></i>
                </button>
            `;
            hiddenContainer.appendChild(ellipsisTestItem);
            const ellipsisWidth = ellipsisTestItem.offsetWidth;
            hiddenContainer.removeChild(ellipsisTestItem);
            
            let accumulatedWidth = ellipsisWidth + gapWidth;
            let visibleCount = 0;
            
            for (let i = itemWidths.length - 1; i >= 0; i--) {
                const itemWidth = itemWidths[i];
                const gapAfterItem = (visibleCount > 0) ? gapWidth : 0;
                const itemTotalWidth = itemWidth + gapAfterItem;
                
                if (accumulatedWidth + itemTotalWidth <= availableWidth) {
                    accumulatedWidth += itemTotalWidth;
                    visibleCount++;
                } else {
                    break;
                }
            }
            
            setVisibleBreadcrumbCount(Math.max(1, visibleCount));
            setIsMeasuring(false);
        };

        let rafId1 = null;
        let rafId2 = null;
        let timer = null;
        
        const isMobileView = window.innerWidth <= 768;
        const delay = isMobileView ? 150 : 50;
        
        rafId1 = requestAnimationFrame(() => {
            rafId2 = requestAnimationFrame(() => {
                timer = setTimeout(measureBreadcrumbs, delay);
            });
        });

        const handleResize = () => {
            setIsMeasuring(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(measureBreadcrumbs);
            });
        };
        window.addEventListener('resize', handleResize);

        return () => {
            if (rafId1) cancelAnimationFrame(rafId1);
            if (rafId2) cancelAnimationFrame(rafId2);
            if (timer) clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, [parents]);

    return (
        <div className="gt-task-overview__heading" ref={headingContainerRef}>
            {/* Hidden container for measuring breadcrumb widths */}
            <div 
                ref={measurementContainerRef}
                className="gt-task-overview__breadcrumbs gt-breadcrumb-measurement-container"
            >
                {parents.map((item, index) => (
                    <div key={index} className="gt-breadcrumb-item">
                        {index > 0 && (
                            <span className="gt-breadcrumb-separator">
                                <svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none">
                                    <path d="M6.99951 5.50042L1.33416 9.68283e-08L-4.05157e-05 1.29536L4.33111 5.50042L-4.04154e-05 9.70464L1.33416 11L6.99951 5.50042Z" fill="#44546F"/>
                                </svg>
                            </span>
                        )}
                        <button className="gt-button-field gt-breadcrumb-button">
                            <span className="gt-breadcrumb-title">{item.title}</span>
                        </button>
                    </div>
                ))}
            </div>

            <nav 
                aria-label="Breadcrumb" 
                className={`gt-task-overview__breadcrumbs gt-breadcrumb-nav-container ${!isMeasuring ? 'gt-breadcrumb-nav-container--visible' : ''}`}
                ref={breadcrumbContainerRef}
            >
                <ol role="list">
                {getTruncatedBreadcrumbs(parents, visibleBreadcrumbCount).map((item, index, array) => (
                    <li key={index} className="gt-breadcrumb-item">
                        {index > 0 && (
                            <span className="gt-breadcrumb-separator" aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none" role="presentation">
                                    <path d="M6.99951 5.50042L1.33416 9.68283e-08L-4.05157e-05 1.29536L4.33111 5.50042L-4.04154e-05 9.70464L1.33416 11L6.99951 5.50042Z" fill="#44546F"/>
                                </svg>
                            </span>
                        )}
                        {item.isEllipsis ? (
                            <>
                                <button 
                                    ref={ellipsisButtonRef}
                                    className="gw-icon-button gw-icon-button--icon"
                                    aria-label={`${parents.length - visibleBreadcrumbCount} hidden parent items. Click to show menu.`}
                                    aria-expanded={showParentsMenu}
                                    aria-haspopup="menu"
                                    onClick={toggleParentsMenu}
                                    data-tooltip-id="breadcrumb-tooltip"
                                    data-tooltip-content="see more"
                                >
                                    <i className="gw-icon-button__icon gw-button__icon--more" aria-hidden="true" />
                                </button>
                                {showParentsMenu && (
                                    <BreadcrumbParentsMenu
                                        hiddenParents={getHiddenParents()}
                                        onParentClick={handleParentBreadcrumb}
                                        onClose={closeParentsMenu}
                                        ellipsisButtonRef={ellipsisButtonRef}
                                    />
                                )}
                            </>
                        ) : (
                            <button 
                                className="gt-button-field gt-breadcrumb-button" 
                                onClick={() => {
                                    if (isMobile && showTooltip) {
                                        handleTooltipClose();
                                        return;
                                    }
                                    handleParentBreadcrumb(item.id);
                                }}
                                onContextMenu={(e) => {
                                    if (!isMobile) {
                                        e.preventDefault();
                                    }
                                }}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                                onTouchMove={handleTouchMove}
                                aria-label={`Navigate to ${item.title}`}
                                aria-current={index === array.length - 1 ? "location" : undefined}
                                data-tooltip-id="breadcrumb-tooltip"
                                data-tooltip-content={item.title}
                            >
                                <span className="gt-breadcrumb-title" aria-hidden="true">{item.title}</span>
                            </button>
                        )}
                    </li>
                ))}
                </ol>
            </nav>
            
            {!isMobile && tooltipReady && !isMeasuring && (
                <Tooltip 
                    key={`breadcrumb-tooltip-${parents.length}`}
                    id="breadcrumb-tooltip"
                    className="gt-breadcrumb-tooltip"
                    delayShow={100}
                    openOnClick={false}
                />
            )}
            
            {isMobile && showTooltip && (
                <>
                    <div 
                        className="gt-breadcrumb-tooltip-backdrop"
                        onClick={handleTooltipClose}
                    />
                    <div 
                        role="tooltip"
                        id="breadcrumb-mobile-tooltip"
                        aria-live="polite"
                        className={`gt-breadcrumb-tooltip-mobile-positioned ${tooltipPosition.positionAbove ? 'gt-breadcrumb-tooltip-mobile-positioned--above' : 'gt-breadcrumb-tooltip-mobile-positioned--below'}`}
                        style={{
                            left: `${tooltipPosition.x}px`,
                            [tooltipPosition.positionAbove ? 'bottom' : 'top']: tooltipPosition.positionAbove 
                                ? `${window.innerHeight - tooltipPosition.y}px` 
                                : `${tooltipPosition.y}px`,
                            maxWidth: `${tooltipPosition.maxWidth || 300}px`
                        }}
                    >
                        {tooltipContent}
                    </div>
                </>
            )}
            
            <div className="gt-task-overview__heading-controls" ref={controlsContainerRef}>
                <button className="gw-icon-button gw-icon-button--icon"
                    aria-label="back"
                    autoFocus=""
                    onClick={() => { closeModalRef.current(); }}>
                    <i className="gw-icon-button__icon gw-button__icon--chevron-left gt-mobile-only" />
                    <i className="gw-icon-button__icon gw-button__icon--close gt-desktop-only" />
                </button>
            </div>
        </div>
    );
};

// Only re-render when parents or isMobile actually change
export default memo(EditModalHeader, (prevProps, nextProps) => {
    if (prevProps.parents.length !== nextProps.parents.length) {
        return false;
    }
    
    const parentsChanged = prevProps.parents.some((parent, index) => 
        parent.id !== nextProps.parents[index]?.id
    );
    
    if (parentsChanged) {
        return false;
    }
    
    if (prevProps.isMobile !== nextProps.isMobile) {
        return false;
    }
    
    return true;
});
